import { useWindowVirtualizer } from "@tanstack/react-virtual";
import moment from "moment/min/moment-with-locales";
import { useCallback, useMemo, useRef, useState } from "react";
import DataDescription from "../DataDescription";
import EmptyRow from "../EmptyRow";
import { DashboardPanel, SearchWithDownloadRow, SoleDescriptionRow, VirtualizedWindowContainer } from "../pageSections";
import useSortableData from "../useSortableData";
import ScheduleCheckSongsHeader from "./ScheduleCheckSongsHeader";
import ScheduleCheckSongsRow from "./ScheduleCheckSongsRow";

export default function ScheduleCheckSongsTable({ data, submittedFormData }) {
    const [filterData, setFilterData] = useState({ searchString: "" });

    const tableContainerRef = useRef(null);

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.artist.toLowerCase();
                    const title = x.title.toLowerCase();
                    const played_at = moment(x.logged_start).format("LTS")?.toString() ?? "";

                    return andParts.every(part => {
                        let orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                const regex = new RegExp(`\\b${exact}\\b`, "i");
                                return regex.test(artist) || regex.test(title) || regex.exact(played_at);
                            }
                            return artist.includes(term) || title.includes(term) || played_at.includes(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo);

    const csvHeaders = ["Artist", "Title", "Scheduled at", "Played at"];
    const csvData = useMemo(
        () =>
            sortedItems?.map(x => [
                x.artist,
                x.title,
                x.scheduled_start ? moment(x.scheduled_start)?.format("LTS") : "-",
                x.logged_start ? moment(x.logged_start).format("LTS") : "-",
            ]),
        [sortedItems],
    );

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 32, []),
        overscan: 30,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <DashboardPanel>
            <DashboardPanel.Title>Schedule</DashboardPanel.Title>
            <div className=" px-4">
                <SoleDescriptionRow>
                    <DataDescription>
                        <DataDescription.Station radioId={submittedFormData.station[0].radio_id} />
                        <DataDescription.Date date={submittedFormData.date} />
                        <DataDescription.HourRange startHour={submittedFormData.hourRange[0]} endHour={submittedFormData.hourRange[1]} />
                    </DataDescription>
                </SoleDescriptionRow>
                <SearchWithDownloadRow
                    filterData={filterData}
                    setFilterData={setFilterData}
                    csvHeaders={csvHeaders}
                    data={csvData}
                    filename={"Schedule Check"}
                />
            </div>
            <DashboardPanel.DataContent>
                <ScheduleCheckSongsHeader requestSort={requestSort} sortConfig={sortConfig} />
                <VirtualizedWindowContainer containerRef={tableContainerRef} virtualizer={virtualizer}>
                    {virtualizer.getVirtualItems().map(virtualRow => {
                        const item = sortedItems[virtualRow.index];
                        if (!item) return <EmptyRow key={virtualRow.key} text="No songs" />;
                        return <ScheduleCheckSongsRow key={virtualRow.key} item={item} virtualRow={virtualRow} virtualizer={virtualizer} />;
                    })}
                </VirtualizedWindowContainer>
            </DashboardPanel.DataContent>
        </DashboardPanel>
    );
}
